import React, { useState } from "react";
import PropTypes from "prop-types";
import { defineMessages, useIntl } from "react-intl";
import { Button, Input, InputNumber, Modal, Space, Switch, Tag, Tooltip } from "antd";
import { ColumnWidthOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { selectors as NODE_CHANNEL_SELECTORS } from "../../ducks/nodeChannels";
import { ENGINE_TYPES, INPUT_STATUS_FILTERS, DESCRIPTION_COLUMN_DEFAULT_WIDTH } from "../../lib/utils/constants";

import themeColor from "../../lib/style/theme";

const { Search } = Input;

const translations = defineMessages({
  search: {
    id: "general.search",
    defaultMessage: "Search",
  },
  filterSearch: {
    id: "SearchInputChannelsList.filterSearch",
    defaultMessage: "Search",
  },
  filterEngineType: {
    id: "SearchInputChannelsList.filterEngineType",
    defaultMessage: "Type",
  },
  filterChannelStatus: {
    id: "SearchInputChannelsList.filterChannelStatus",
    defaultMessage: "Channel status",
  },
  filterDescription: {
    id: "SearchInputChannelsList.filterDescription",
    defaultMessage: "Description",
  },
  showDescriptionColumn: {
    id: "SearchInputChannelsList.showDescription",
    defaultMessage: "Description",
  },
  columnWidthTitle: {
    id: "SearchInputChannelsList.columnWidthTitle",
    defaultMessage: "Description column width",
  },
  columnWidthTooltip: {
    id: "SearchInputChannelsList.columnWidthTooltip",
    defaultMessage: "Set column width",
  },
});

const ENGINE_TYPE_LABEL = {
  [ENGINE_TYPES.DIRECT]: "Direct",
  [ENGINE_TYPES.TRANSCODING]: "Transcoding",
  [ENGINE_TYPES.PLAYOUT]: "Playout",
};

const CHANNEL_STATUS_LABEL = INPUT_STATUS_FILTERS.reduce((acc, { value, text }) => {
  acc[value] = text;

  return acc;
}, {});

const parseCommaList = (raw) => (raw && raw.length > 0 ? raw.split(",").filter(Boolean) : []);

export default function SearchInputChannelsList({
  showDescriptionColumn = false,
  onShowDescriptionColumnChange,
  descriptionColumnWidth = DESCRIPTION_COLUMN_DEFAULT_WIDTH,
  onDescriptionColumnWidthChange,
  showDescriptionColumnControl = false,
}) {
  const { formatMessage } = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const isLoadingChannelList = useSelector(NODE_CHANNEL_SELECTORS.getLoadingChannels);
  const sortedNodeChannels = useSelector(NODE_CHANNEL_SELECTORS.getSortedChannels);
  const [widthModalOpen, setWidthModalOpen] = useState(false);
  const [widthInput, setWidthInput] = useState(descriptionColumnWidth);

  const search = searchParams.get("search") || "";
  const channelDescriptionSearch = searchParams.get("channelDescription") || "";
  const engineTypeRaw = searchParams.get("engineType");
  const inputStatusRaw = searchParams.get("inputStatus");

  const engineTypeValues = parseCommaList(engineTypeRaw);
  const inputStatusValues = parseCommaList(inputStatusRaw);

  const handleSearchChannels = (searchValue) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (searchValue) {
          next.set("search", searchValue);
        } else {
          next.delete("search");
        }

        return next;
      },
      { replace: true }
    );
  };

  const handleChange = (updatedValue) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (updatedValue === "") {
          next.delete("search");
        } else {
          next.set("search", updatedValue);
        }

        return next;
      },
      { replace: true }
    );
  };

  const removeParam = (paramKey) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(paramKey);

        return next;
      },
      { replace: true }
    );
  };

  const removeFromCommaParam = (paramKey, valueToRemove) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const raw = next.get(paramKey);
        if (!raw) {
          return next;
        }

        const remaining = raw.split(",").filter((v) => v !== valueToRemove);
        if (remaining.length === 0) {
          next.delete(paramKey);
        } else {
          next.set(paramKey, remaining.join(","));
        }

        return next;
      },
      { replace: true }
    );
  };

  const handleDescriptionColumnToggle = (checked) => {
    if (!checked) {
      removeParam("channelDescription");
    }

    onShowDescriptionColumnChange?.(checked);
  };

  const handleOpenWidthModal = () => {
    setWidthInput(descriptionColumnWidth);
    setWidthModalOpen(true);
  };

  const handleWidthModalOk = () => {
    const value = Number(widthInput);
    if (value && value >= 150) {
      onDescriptionColumnWidthChange?.(value);
    }
    setWidthModalOpen(false);
  };

  return (
    <StyledRow>
      <>
        <span>{formatMessage(translations.search)} </span>
        <StyledSearch
          disabled={sortedNodeChannels.length === 0}
          loading={isLoadingChannelList}
          onSearch={handleSearchChannels}
          allowClear
          value={search}
          onChange={(event) => handleChange(event.target.value)}
        />
      </>
      {showDescriptionColumnControl ? (
        <>
          <StyledSwitchLabel>
            <Switch size="small" checked={showDescriptionColumn} onChange={handleDescriptionColumnToggle} />
            <span>{formatMessage(translations.showDescriptionColumn)}</span>
          </StyledSwitchLabel>
          {showDescriptionColumn ? (
            <Tooltip title={formatMessage(translations.columnWidthTooltip)}>
              <Button
                color="primary"
                variant="outlined"
                shape="circle"
                size="small"
                icon={<ColumnWidthOutlined />}
                onClick={handleOpenWidthModal}
              />
            </Tooltip>
          ) : null}
          <Modal
            title={formatMessage(translations.columnWidthTitle)}
            open={widthModalOpen}
            onOk={handleWidthModalOk}
            onCancel={() => setWidthModalOpen(false)}
            destroyOnClose
            width={250}
          >
            <StyledModalContent>
              <InputNumber
                min={150}
                value={widthInput}
                onChange={setWidthInput}
                onPressEnter={handleWidthModalOk}
                addonAfter="px"
                style={{ width: "100%" }}
              />
            </StyledModalContent>
          </Modal>
        </>
      ) : null}
      <StyledTagSpace align="center" wrap={false} size={[6, 6]}>
        {search ? (
          <Tag
            closable
            onClose={() => removeParam("search")}
            title={`${formatMessage(translations.filterSearch)}: ${search}`}
          >
            {formatMessage(translations.filterSearch)}: {search}
          </Tag>
        ) : null}
        {showDescriptionColumn && channelDescriptionSearch ? (
          <Tag
            closable
            onClose={() => removeParam("channelDescription")}
            title={`${formatMessage(translations.filterDescription)}: ${channelDescriptionSearch}`}
          >
            {formatMessage(translations.filterDescription)}: {channelDescriptionSearch}
          </Tag>
        ) : null}
        {engineTypeValues.map((value) => (
          <Tag
            key={`engineType-${value}`}
            closable
            onClose={() => removeFromCommaParam("engineType", value)}
            title={ENGINE_TYPE_LABEL[value] || value}
          >
            {formatMessage(translations.filterEngineType)}: {ENGINE_TYPE_LABEL[value] || value}
          </Tag>
        ))}
        {inputStatusValues.map((value) => (
          <Tag
            key={`inputStatus-${value}`}
            closable
            onClose={() => removeFromCommaParam("inputStatus", value)}
            title={CHANNEL_STATUS_LABEL[value] || value}
          >
            {formatMessage(translations.filterChannelStatus)}: {CHANNEL_STATUS_LABEL[value] || value}
          </Tag>
        ))}
      </StyledTagSpace>
    </StyledRow>
  );
}

const StyledRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px 12px;
  width: 100%;
  min-width: 0;
  overflow-x: auto;
`;

const StyledSwitchLabel = styled.label`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  margin: 0;
  white-space: nowrap;
  cursor: pointer;
`;

const StyledSearch = styled(Search)`
  max-width: 300px;

  .anticon-close-circle {
    margin: 0 auto;
  }
  .ant-input-search-button {
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${(props) => (props.disabled ? "" : themeColor.orange)};

    &:hover {
      background: ${themeColor.orangeHover} !important;
    }
  }
  .anticon-search {
    margin: 0 auto;
    color: #fff;
  }
`;

const StyledTagSpace = styled(Space)`
  flex: 0 1 auto;
  min-width: 0;
  justify-content: flex-start;
  flex-wrap: nowrap;
`;

const StyledModalContent = styled.div`
  padding: 16px 0;
`;

SearchInputChannelsList.propTypes = {
  showDescriptionColumn: PropTypes.bool,
  onShowDescriptionColumnChange: PropTypes.func,
  descriptionColumnWidth: PropTypes.number,
  onDescriptionColumnWidthChange: PropTypes.func,
  showDescriptionColumnControl: PropTypes.bool,
};

SearchInputChannelsList.defaultProps = {
  showDescriptionColumn: false,
  onShowDescriptionColumnChange: null,
  descriptionColumnWidth: DESCRIPTION_COLUMN_DEFAULT_WIDTH,
  onDescriptionColumnWidthChange: null,
  showDescriptionColumnControl: false,
};
